import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { UNSAFE_NavigationContext } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowDownward, ArrowUpward, ErrorOutline } from '@mui/icons-material';
import PageHeader from './PageHeader';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import { formatDateWithSeconds } from '../utils/dateUtils';
import {
  getAttendanceDailyReportSettings,
  saveAttendanceDailyReportRecipients,
  saveAttendanceDailyReportSettings,
  sendAttendanceDailyReportNow,
} from '../utils/api';

const errorText = (error, fallback) => error?.detail || error?.message || fallback;

const ROLE_ORDER = ['employee', 'vendor', 'visitor', 'intrusion'];
const ROLE_LABELS = {
  employee: 'Employee',
  vendor: 'Vendor',
  visitor: 'Visitor',
  intrusion: 'Intrusion',
};

const emptyFieldMap = () => ({ employee: [], vendor: [], visitor: [], intrusion: [] });

const savedFormKey = (form) => JSON.stringify({
  enabled: Boolean(form.enabled),
  sendTime: String(form.sendTime || '00:05'),
  includeEmployee: Boolean(form.includeEmployee),
  includeVendor: Boolean(form.includeVendor),
  includeVisitor: Boolean(form.includeVisitor),
  includeIntrusion: Boolean(form.includeIntrusion),
  selectedFields: ROLE_ORDER.reduce((acc, role) => {
    const values = form.selectedFields?.[role];
    acc[role] = Array.isArray(values) ? values.map((item) => String(item)) : [];
    return acc;
  }, {}),
});

const formatSendLabel = (hhmm) => {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(String(hhmm || ''));
  if (!match) return 'Every day at 12:05 AM IST, for the previous day';
  const hours = Number(match[1]);
  const minutes = match[2];
  const suffix = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  return `Every day at ${hour12}:${minutes} ${suffix} IST, for the previous day`;
};

const DailyReport = () => {
  const { confirm } = useConfirmDialog();
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const savedFormRef = useRef('');
  const unsavedResolverRef = useRef(null);
  const [unsavedPrompt, setUnsavedPrompt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [sendTime, setSendTime] = useState('00:05');
  const [recipientsOpen, setRecipientsOpen] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [maxRecipients, setMaxRecipients] = useState(50);
  const [recipientDraft, setRecipientDraft] = useState('');
  const [includeEmployee, setIncludeEmployee] = useState(true);
  const [includeVendor, setIncludeVendor] = useState(false);
  const [includeVisitor, setIncludeVisitor] = useState(false);
  const [includeIntrusion, setIncludeIntrusion] = useState(false);
  const [vendorEnabled, setVendorEnabled] = useState(false);
  const [visitorEnabled, setVisitorEnabled] = useState(false);
  const [fields, setFields] = useState(emptyFieldMap);
  const [selectedFields, setSelectedFields] = useState(emptyFieldMap);
  const [columnRole, setColumnRole] = useState('employee');
  const [lastStatus, setLastStatus] = useState(null);
  const [lastError, setLastError] = useState('');
  const [lastSentAt, setLastSentAt] = useState(null);
  const [lastReportDate, setLastReportDate] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [errorLogOpen, setErrorLogOpen] = useState(false);

  const show = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const applyStatus = (data) => {
    setLastStatus(data?.last_status || null);
    const rawError = String(data?.last_error || '');
    const hidesEnv = /EMAIL_|SMTP_|EXCEPTION_|ENV/i.test(rawError);
    setLastError(hidesEnv ? '' : rawError);
    setLastSentAt(data?.last_sent_at || null);
    setLastReportDate(data?.last_report_date || null);
  };

  const applySettings = (data, { statusOnly = false } = {}) => {
    if (statusOnly) {
      applyStatus(data);
      return;
    }
    const catalog = data?.fields && typeof data.fields === 'object' ? data.fields : {};
    const nextFields = emptyFieldMap();
    const nextSelected = emptyFieldMap();
    ROLE_ORDER.forEach((role) => {
      nextFields[role] = Array.isArray(catalog[role]) ? catalog[role] : [];
      nextSelected[role] = Array.isArray(data?.selected_fields?.[role]) ? data.selected_fields[role] : [];
    });
    const nextEnabled = Boolean(data?.enabled);
    const nextSendTime = data?.send_time || '00:05';
    const nextVendorEnabled = Boolean(data?.vendor_management_enabled);
    const nextVisitorEnabled = Boolean(data?.visitor_management_enabled);
    const nextIncludeEmployee = data?.include_employee !== false;
    const nextIncludeVendor = Boolean(data?.include_vendor) && nextVendorEnabled;
    const nextIncludeVisitor = Boolean(data?.include_visitor) && nextVisitorEnabled;
    const nextIncludeIntrusion = Boolean(data?.include_intrusion);
    setFields(nextFields);
    setEnabled(nextEnabled);
    setSendTime(nextSendTime);
    setRecipients(Array.isArray(data?.recipients) ? data.recipients : []);
    setMaxRecipients(Number(data?.max_recipients) > 0 ? Number(data.max_recipients) : 50);
    setIncludeEmployee(nextIncludeEmployee);
    setIncludeVendor(nextIncludeVendor);
    setIncludeVisitor(nextIncludeVisitor);
    setIncludeIntrusion(nextIncludeIntrusion);
    setVendorEnabled(nextVendorEnabled);
    setVisitorEnabled(nextVisitorEnabled);
    setSelectedFields(nextSelected);
    savedFormRef.current = savedFormKey({
      enabled: nextEnabled,
      sendTime: nextSendTime,
      includeEmployee: nextIncludeEmployee,
      includeVendor: nextIncludeVendor,
      includeVisitor: nextIncludeVisitor,
      includeIntrusion: nextIncludeIntrusion,
      selectedFields: nextSelected,
    });
    applyStatus(data);
  };

  const loadSettings = useCallback(async ({ silent = false, statusOnly = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const data = await getAttendanceDailyReportSettings();
      applySettings(data, { statusOnly });
    } catch (error) {
      show(errorText(error, 'Failed to load daily report settings'), 'error');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const includedRoles = useMemo(() => {
    const roles = [];
    if (includeEmployee) roles.push('employee');
    if (vendorEnabled && includeVendor) roles.push('vendor');
    if (visitorEnabled && includeVisitor) roles.push('visitor');
    if (includeIntrusion) roles.push('intrusion');
    return roles;
  }, [includeEmployee, includeVendor, includeVisitor, includeIntrusion, vendorEnabled, visitorEnabled]);

  const activeColumnRole = includedRoles.includes(columnRole) ? columnRole : (includedRoles[0] || '');

  const visibleFields = useMemo(
    () => (fields[activeColumnRole] || []).filter((field) => field.enabled !== false),
    [fields, activeColumnRole],
  );

  const orderedFields = useMemo(() => {
    const chosen = selectedFields[activeColumnRole] || [];
    const byName = new Map(visibleFields.map((field) => [field.field, field]));
    const selected = chosen.map((name) => byName.get(name)).filter(Boolean);
    const rest = visibleFields.filter((field) => !chosen.includes(field.field));
    return [...selected, ...rest];
  }, [selectedFields, visibleFields, activeColumnRole]);

  const addRecipient = () => {
    const next = recipientDraft.trim().toLowerCase();
    if (!next) {
      show('Enter an email address', 'error');
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(next)) {
      show('Enter a valid email address', 'error');
      return;
    }
    if (recipients.includes(next)) {
      show('This email is already added', 'error');
      return;
    }
    if (recipients.length >= maxRecipients) {
      show(`At most ${maxRecipients} recipients are allowed`, 'error');
      return;
    }
    setRecipientDraft('');
    persistRecipients([...recipients, next]);
  };

  const persistRecipients = async (next) => {
    const previous = recipients;
    setRecipients(next);
    try {
      const data = await saveAttendanceDailyReportRecipients(next);
      setRecipients(Array.isArray(data?.recipients) ? data.recipients : next);
    } catch (error) {
      setRecipients(previous);
      show(errorText(error, 'Could not save the recipient list'), 'error');
    }
  };

  const removeRecipient = (email) => {
    persistRecipients(recipients.filter((item) => item !== email));
  };

  const toggleField = (fieldName, checked) => {
    setSelectedFields((prev) => {
      const current = prev[activeColumnRole] || [];
      const next = checked
        ? (current.includes(fieldName) ? current : [...current, fieldName])
        : current.filter((name) => name !== fieldName);
      return { ...prev, [activeColumnRole]: next };
    });
  };

  const selectAllFields = () => {
    setSelectedFields((prev) => ({
      ...prev,
      [activeColumnRole]: visibleFields.map((field) => field.field),
    }));
  };

  const resetFields = () => {
    setSelectedFields((prev) => ({ ...prev, [activeColumnRole]: [] }));
  };

  const moveField = (index, direction) => {
    setSelectedFields((prev) => {
      const current = [...(prev[activeColumnRole] || [])];
      const nextIndex = direction === 'up' ? index - 1 : index + 1;
      if (nextIndex < 0 || nextIndex >= current.length) return prev;
      const [item] = current.splice(index, 1);
      current.splice(nextIndex, 0, item);
      return { ...prev, [activeColumnRole]: current };
    });
  };

  const currentFormKey = savedFormKey({
    enabled,
    sendTime,
    includeEmployee,
    includeVendor: vendorEnabled ? includeVendor : false,
    includeVisitor: visitorEnabled ? includeVisitor : false,
    includeIntrusion,
    selectedFields,
  });
  const hasUnsavedChanges = Boolean(savedFormRef.current) && currentFormKey !== savedFormRef.current;

  const askUnsaved = (purpose) => new Promise((resolve) => {
    if (unsavedResolverRef.current) {
      resolve('cancel');
      return;
    }
    unsavedResolverRef.current = resolve;
    setUnsavedPrompt(purpose);
  });

  const closeUnsaved = (choice) => {
    const resolve = unsavedResolverRef.current;
    unsavedResolverRef.current = null;
    setUnsavedPrompt(null);
    if (resolve) resolve(choice);
  };

  const payload = () => ({
    enabled,
    send_time: sendTime,
    recipients,
    include_employee: includeEmployee,
    include_vendor: vendorEnabled ? includeVendor : false,
    include_visitor: visitorEnabled ? includeVisitor : false,
    include_intrusion: includeIntrusion,
    selected_fields: selectedFields,
  });

  const handleSave = async () => {
    if (enabled && recipients.length === 0) {
      show('Add at least one recipient before enabling the daily mail', 'error');
      return false;
    }
    if (includedRoles.length === 0) {
      show('Select at least one of Employee, Vendor, Visitor, or Intrusion', 'error');
      return false;
    }
    const missing = includedRoles.find((role) => !(selectedFields[role] || []).length);
    if (missing) {
      show(`Select at least one column for ${ROLE_LABELS[missing]}`, 'error');
      setColumnRole(missing);
      return false;
    }
    setSaving(true);
    try {
      const data = await saveAttendanceDailyReportSettings(payload());
      applySettings(data);
      show('Daily report settings saved');
      return true;
    } catch (error) {
      show(errorText(error, 'Failed to save daily report settings'), 'error');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSendNow = async () => {
    const accepted = await confirm({
      title: 'Send yesterday’s report',
      message: 'This emails the previous day’s attendance workbook using the saved settings.',
      confirmLabel: 'Send',
    });
    if (!accepted) return;
    setSending(true);
    try {
      const result = await sendAttendanceDailyReportNow();
      show(result?.message || 'Report sent');
      await loadSettings({ silent: true });
    } catch (error) {
      show(errorText(error, 'Failed to send the report'), 'error');
      await loadSettings({ silent: true });
    } finally {
      setSending(false);
    }
  };

  const hasUnsavedRef = useRef(false);
  hasUnsavedRef.current = hasUnsavedChanges;
  const askUnsavedRef = useRef(askUnsaved);
  askUnsavedRef.current = askUnsaved;
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  useEffect(() => {
    const onBeforeUnload = (event) => {
      if (!hasUnsavedRef.current) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  useEffect(() => {
    if (!navigator || typeof navigator.push !== 'function' || typeof navigator.replace !== 'function') return undefined;
    const originalPush = navigator.push.bind(navigator);
    const originalReplace = navigator.replace.bind(navigator);
    const originalGo = typeof navigator.go === 'function' ? navigator.go.bind(navigator) : null;
    let disposed = false;
    let prompting = false;

    const guard = (original, args) => {
      if (prompting) return;
      prompting = true;
      askUnsavedRef.current('leave').then(async (choice) => {
        prompting = false;
        if (disposed || (choice !== 'save' && choice !== 'discard')) return;
        if (choice === 'save') {
          const saved = await handleSaveRef.current();
          if (!saved || disposed) return;
        }
        hasUnsavedRef.current = false;
        original(...args);
      });
    };

    navigator.push = (...args) => {
      if (!hasUnsavedRef.current) {
        originalPush(...args);
        return;
      }
      guard(originalPush, args);
    };
    navigator.replace = (...args) => {
      if (!hasUnsavedRef.current) {
        originalReplace(...args);
        return;
      }
      guard(originalReplace, args);
    };
    if (originalGo) {
      navigator.go = (delta) => {
        if (!hasUnsavedRef.current) {
          originalGo(delta);
          return;
        }
        guard(originalGo, [delta]);
      };
    }
    return () => {
      disposed = true;
      navigator.push = originalPush;
      navigator.replace = originalReplace;
      if (originalGo) navigator.go = originalGo;
    };
  }, [navigator]);

  const includeOptions = [
    { key: 'employee', label: 'Employee', checked: includeEmployee, onChange: setIncludeEmployee },
    ...(vendorEnabled ? [{ key: 'vendor', label: 'Vendor', checked: includeVendor, onChange: setIncludeVendor }] : []),
    ...(visitorEnabled ? [{ key: 'visitor', label: 'Visitor', checked: includeVisitor, onChange: setIncludeVisitor }] : []),
    { key: 'intrusion', label: 'Intrusion', checked: includeIntrusion, onChange: setIncludeIntrusion },
  ];

  const chosenFields = selectedFields[activeColumnRole] || [];

  return (
    <Container maxWidth="2200" disableGutters sx={{ px: 0 }}>
      <PageHeader
        title="Daily Report"
        subtitle="Email the previous day’s attendance workbook"
        icon={null}
      />
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(320px, 420px) minmax(0, 1fr)' },
            gap: 2,
            alignItems: 'start',
          }}
        >
          <Stack spacing={2}>
            <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Send daily mail</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatSendLabel(sendTime)}
                  </Typography>
                </Box>
                <Switch checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />
              </Stack>
              <TextField
                type="time"
                size="small"
                label="Send at (IST)"
                value={sendTime}
                onChange={(event) => setSendTime(event.target.value || '00:05')}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 60 }}
                sx={{ mt: 1.5, width: 180 }}
              />
              {lastStatus && (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                  <Typography variant="caption" color={lastStatus === 'success' ? 'success.main' : 'error.main'}>
                    {lastStatus === 'success' ? 'Last run sent' : 'Last run failed'}
                    {lastSentAt ? ` · ${formatDateWithSeconds(lastSentAt)}` : ''}
                  </Typography>
                  {lastStatus !== 'success' && lastError && (
                    <Button size="small" color="error" onClick={() => setErrorLogOpen(true)} sx={{ minWidth: 0, px: 0.5 }}>
                      View error
                    </Button>
                  )}
                </Stack>
              )}
            </Paper>

            <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Recipients</Typography>
                <Typography variant="caption" color="text.secondary">
                  {recipients.length} recipient{recipients.length === 1 ? '' : 's'} configured
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Email address"
                  value={recipientDraft}
                  onChange={(event) => setRecipientDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      addRecipient();
                    }
                  }}
                />
                <Button variant="contained" onClick={addRecipient} sx={{ minWidth: 88, px: 2 }}>+ Add</Button>
              </Stack>
              <Button
                variant="text"
                onClick={() => setRecipientsOpen(true)}
                sx={{ mt: 1, px: 0 }}
              >
                Configured emails ({recipients.length})
              </Button>
            </Paper>

            <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>Include</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                {includeOptions.map((option) => (
                  <Box
                    key={option.key}
                    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, px: 1, py: 0.25 }}
                  >
                    <FormControlLabel
                      sx={{ m: 0, width: '100%' }}
                      control={<Checkbox checked={option.checked} onChange={(event) => option.onChange(event.target.checked)} />}
                      label={option.label}
                    />
                  </Box>
                ))}
              </Box>
            </Paper>

            <Stack spacing={1} alignItems="flex-start">
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Button variant="contained" onClick={() => handleSave()} disabled={saving || sending} sx={{ height: 40, whiteSpace: 'nowrap' }}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
                <Button variant="outlined" onClick={handleSendNow} disabled={saving || sending || recipients.length === 0 || hasUnsavedChanges} sx={{ height: 40, whiteSpace: 'nowrap' }}>
                  {sending ? 'Sending...' : 'Send yesterday’s report'}
                </Button>
              </Stack>
              {hasUnsavedChanges && (
                <Typography variant="body2" color="warning.main">Changes are not saved</Typography>
              )}
            </Stack>
          </Stack>

          <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2, minHeight: { lg: '100%' } }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Columns</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button size="small" onClick={selectAllFields}>Select all</Button>
                <Typography variant="body2" color="text.disabled">|</Typography>
                <Button size="small" color="inherit" onClick={resetFields}>Reset</Button>
              </Stack>
            </Stack>
            {includedRoles.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Select a role under Include. Each selected role gets its own Excel sheet.
              </Typography>
            ) : (
            <Tabs
              value={activeColumnRole}
              onChange={(_event, value) => setColumnRole(value)}
              variant="scrollable"
              sx={{ mt: 1, minHeight: 40, borderBottom: '1px solid', borderColor: 'divider' }}
            >
              {includedRoles.map((role) => (
                <Tab key={role} value={role} label={ROLE_LABELS[role]} sx={{ minHeight: 40, fontWeight: 600 }} />
              ))}
            </Tabs>
            )}
            {includedRoles.length > 0 && (
            <Stack spacing={1} sx={{ mt: 2, maxHeight: { xs: 480, lg: 'calc(100vh - 220px)' }, overflow: 'auto', pr: 0.5 }}>
              {orderedFields.map((field) => {
                const selected = chosenFields.includes(field.field);
                const selectedIndex = chosenFields.indexOf(field.field);
                return (
                  <Box
                    key={field.field}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      pl: 0.5,
                      pr: 1,
                      py: 0.25,
                    }}
                  >
                    <Checkbox
                      checked={selected}
                      onChange={(event) => toggleField(field.field, event.target.checked)}
                    />
                    <Typography sx={{ flex: 1 }}>{field.label || field.field}</Typography>
                    <IconButton size="small" onClick={() => moveField(selectedIndex, 'up')} disabled={!selected || selectedIndex <= 0}>
                      <ArrowUpward fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => moveField(selectedIndex, 'down')}
                      disabled={!selected || selectedIndex >= chosenFields.length - 1}
                    >
                      <ArrowDownward fontSize="small" />
                    </IconButton>
                  </Box>
                );
              })}
            </Stack>
            )}
          </Paper>
        </Box>
      )}

      <Dialog open={recipientsOpen} onClose={() => setRecipientsOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Configured emails</DialogTitle>
        <DialogContent>
          {recipients.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No emails added yet.</Typography>
          ) : (
            <Stack spacing={1} sx={{ pt: 0.5 }}>
              {recipients.map((email) => (
                <Chip
                  key={email}
                  label={email}
                  onDelete={() => removeRecipient(email)}
                  sx={{ justifyContent: 'space-between', borderRadius: 2, height: 36, '& .MuiChip-label': { flex: 1 } }}
                />
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecipientsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(unsavedPrompt)} onClose={() => closeUnsaved('cancel')} maxWidth="xs" fullWidth>
        <DialogTitle>Unsaved changes</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            These changes are not saved. Save them before leaving this page?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => closeUnsaved('cancel')}>Cancel</Button>
          <Button onClick={() => closeUnsaved('discard')}>Don't save</Button>
          <Button variant="contained" onClick={() => closeUnsaved('save')}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={errorLogOpen} onClose={() => setErrorLogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorOutline color="error" fontSize="small" />
          Last run error
          {lastReportDate ? ` · ${lastReportDate}` : ''}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">{lastError}</Typography>
        </DialogContent>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default DailyReport;
